import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { X, FileText, Scale, Sparkles } from "lucide-react";

export interface EditableFinding {
  class: string;
  confidence: number;
  description?: string;
  code_citations?: string[];
}

interface FindingEditorProps {
  finding: EditableFinding;
  index: number;
  jobMode?: 'retail' | 'insurance';
  onChange: (updated: EditableFinding) => void;
  onPlacePin?: () => void;
}

export const FindingEditor = ({ finding, index, jobMode = 'insurance', onChange, onPlacePin }: FindingEditorProps) => {
  const [description, setDescription] = useState(finding.description || '');
  const [codes, setCodes] = useState<string[]>(finding.code_citations || []);
  const [newCode, setNewCode] = useState('');

  useEffect(() => {
    setDescription(finding.description || '');
    setCodes(finding.code_citations || []);
  }, [finding]);

  const commit = (nextDesc: string, nextCodes: string[]) => {
    onChange({ ...finding, description: nextDesc, code_citations: nextCodes });
  };

  const addCode = () => {
    const c = newCode.trim();
    if (!c) return;
    const next = [...codes, c];
    setCodes(next);
    setNewCode('');
    commit(description, next);
  };

  const removeCode = (i: number) => {
    const next = codes.filter((_, idx) => idx !== i);
    setCodes(next);
    commit(description, next);
  };

  return (
    <div className="border rounded-lg p-3 space-y-3 bg-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">#{index + 1}</Badge>
          <span className="font-medium text-sm capitalize">{finding.class.replace(/-/g, ' ')}</span>
          <Badge variant="secondary" className="text-xs">{(finding.confidence * 100).toFixed(0)}%</Badge>
        </div>
        {onPlacePin && (
          <Button size="sm" variant="ghost" onClick={onPlacePin} title="Place pin">📍</Button>
        )}
      </div>

      <div className="space-y-1">
        <Label className="text-xs flex items-center gap-1 text-muted-foreground">
          <FileText className="w-3 h-3" /> Description
          <Badge variant="outline" className="ml-auto text-[10px] gap-1">
            <Sparkles className="w-2.5 h-2.5" />
            {jobMode === 'insurance' ? 'Insurance voice' : 'Retail voice'}
          </Badge>
        </Label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={() => commit(description, codes)}
          placeholder={jobMode === 'insurance'
            ? 'Two declarative sentences citing damage + IRC code…'
            : 'Plain-English description for the homeowner…'}
          className="text-xs min-h-[70px] resize-y"
        />
      </div>

      {jobMode === 'insurance' && (
        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1 text-muted-foreground">
            <Scale className="w-3 h-3" /> Code citations
          </Label>
          <div className="flex flex-wrap gap-1">
            {codes.map((c, i) => (
              <Badge key={i} variant="secondary" className="text-xs gap-1 pl-2 pr-1">
                {c}
                <button type="button" onClick={() => removeCode(i)} className="hover:bg-background rounded">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {codes.length === 0 && (
              <span className="text-xs text-muted-foreground italic">No codes yet</span>
            )}
          </div>
          <div className="flex gap-1">
            <Input
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCode(); } }}
              placeholder="e.g. IRC R905.2.8"
              className="text-xs h-7"
            />
            <Button size="sm" variant="outline" className="h-7" onClick={addCode}>Add</Button>
          </div>
        </div>
      )}
    </div>
  );
};
